import { type ReactNode } from 'react';
import { SidebarAccount, useSidebar } from '@vitness/layouts';
import { ThemeSwitcher, Button, Avatar } from '@vitness/ui';
import { Settings, RefreshCw, UserCog, LogOut } from '@vitness/icons';
import { useNavigate } from 'react-router-dom';
import { useClerk, useUser } from '@clerk/react';
import { useStore } from '../lib/store';

/**
 * Sidebar footer identity.
 *
 * Uses the design system's SidebarAccount, which already handles the collapsed
 * rail properly (avatar-only, avatar becomes the popover trigger).
 *
 * Identity comes from Clerk's `useUser`, not from `/api/me`. The worker only
 * knows what's in the session JWT, and Clerk's default token carries `sub` but
 * no email claim — so `/api/me` answered with the raw `user_…` id and the
 * sidebar displayed that. The client already holds the full user object; asking
 * the server for a worse answer was the whole bug.
 *
 * Account actions mirror @vitness/auth's AccountMenu — manage account opens
 * Clerk's profile modal, sign out ends the session — so the control behaves the
 * same way here as in the showcase.
 */
export function AccountChip() {
  const { state, isMobile } = useSidebar();
  const collapsed = state === 'collapsed' && !isMobile;
  const navigate = useNavigate();
  const clerk = useClerk();
  const { user } = useUser();
  const sync = useStore(s => s.sync);
  const syncing = useStore(s => s.syncing);

  const email = user?.primaryEmailAddress?.emailAddress;
  // `fullName` is null until first and last name are set on the Clerk user, so
  // fall through rather than rendering "null".
  const name = user?.fullName || user?.username || email || 'Account';

  return (
    <SidebarAccount
      collapsed={collapsed}
      name={name}
      meta="Clerk"
      avatar={
        <Avatar
          name={name}
          email={email}
          imageUrl={user?.hasImage ? user.imageUrl : undefined}
          size={26}
        />
      }
      menuTitle="Account"
      menuWidth={264}
      menu={
        <Menu
          onManage={() => clerk.openUserProfile()}
          onSignOut={() => { void clerk.signOut(); }}
          onSettings={() => navigate('/settings')}
          onSync={sync}
          syncing={syncing}
          email={email}
        />
      }
    />
  );
}

function Menu({ onManage, onSignOut, onSettings, onSync, syncing, email }: {
  onManage: () => void;
  onSignOut: () => void;
  onSettings: () => void;
  onSync: () => void;
  syncing: boolean;
  email: string | undefined;
}): ReactNode {
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {/* Account actions first. Sign out is what you go looking for, and it was
          absent entirely before — Cloudflare Access owned identity, so there was
          nothing to sign out of. */}
      <div style={{ display: 'grid', gap: 4 }}>
        <Button size="sm" variant="ghost" onClick={onManage} style={{ justifyContent: 'flex-start' }}>
          <UserCog size={13} /> Manage account
        </Button>
        <Button size="sm" variant="ghost" onClick={onSignOut} style={{ justifyContent: 'flex-start' }}>
          <LogOut size={13} /> Sign out
        </Button>
      </div>

      <div style={{ height: 1, background: 'var(--vn-border-default)' }} />

      <div style={{ display: 'grid', gap: 4 }}>
        <Button size="sm" variant="ghost" onClick={onSettings} style={{ justifyContent: 'flex-start' }}>
          <Settings size={13} /> Settings
        </Button>
        <Button size="sm" variant="ghost" onClick={onSync} disabled={syncing} style={{ justifyContent: 'flex-start' }}>
          <RefreshCw size={13} /> {syncing ? 'Syncing…' : 'Sync from Notion'}
        </Button>
      </div>

      <div style={{ height: 1, background: 'var(--vn-border-default)' }} />

      {/* Appearance last, matching AccountMenu, where the ThemeSwitcher is the
          popover footer. */}
      <ThemeSwitcher heading="Appearance" />

      {email && (
        <div style={{
          fontFamily: 'var(--vn-font-mono)', fontSize: 9, color: 'var(--vn-text-disabled)',
          paddingTop: 6, borderTop: '1px solid var(--vn-border-default)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {email}
        </div>
      )}
    </div>
  );
}
